/**
 * The workspace's right-hand editor column, a flex sibling of the canvas.
 *
 * It takes width from the canvas instead of covering it, so the part of the
 * drawing an SME is editing stays visible beside the form. The body scrolls and
 * the footer does not, so the save action is on screen however long the form is.
 * Under an `EditorDockSlotContext` it renders into the workspace's dock column,
 * wherever the editor sits in the workspace's component tree.
 */
import { useContext, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { AppIcon } from '../../components/LucideIcon';
import { DockFooterSlotContext, EditorDockSlotContext } from './dockSlots';
import './editor-dock.css';

export default function EditorDock({
  title,
  subtitle,
  headerActions,
  onBack,
  onClose,
  confirmDiscardOnEscape = false,
  hidden = false,
  onKeyDownCapture,
  footer,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Controls in the header, left of the close button. */
  headerActions?: ReactNode;
  /** Shown as "‹ Back" when this view sits on top of another; Escape inside the dock calls it instead of `onClose`. */
  onBack?: () => void;
  /** Called by the close button, and by Escape when there is no `onBack`. Omit it for a dock that has no close action. */
  onClose?: () => void;
  /** Escape asks before it calls `onClose`, for an editor whose close discards what the SME typed. */
  confirmDiscardOnEscape?: boolean;
  /** Keeps a covered view mounted, with its draft, while the view on top of it shows. */
  hidden?: boolean;
  /** Shortcut keys the view reads before its controls do. */
  onKeyDownCapture?: (event: KeyboardEvent<HTMLElement>) => void;
  /** Pinned below the scrolling body; normally a `DockFooter`. */
  footer?: ReactNode;
  children: ReactNode;
}) {
  const workspaceSlot = useContext(EditorDockSlotContext);
  const [footerSlot, setFooterSlot] = useState<HTMLDivElement | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const focusBeforeConfirmRef = useRef<HTMLElement | null>(null);

  const keepEditing = () => {
    setConfirmingDiscard(false);
    focusBeforeConfirmRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    // A control that consumed Escape itself (a dropdown, a quick-entry step) keeps it.
    const escape = onBack ?? onClose;
    if (event.key !== 'Escape' || !escape || event.defaultPrevented) return;
    event.preventDefault();
    if (confirmingDiscard) {
      keepEditing();
      return;
    }
    if (confirmDiscardOnEscape) {
      focusBeforeConfirmRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setConfirmingDiscard(true);
      return;
    }
    escape();
  };

  const dock = (
    <aside className="pnid-editor-dock" data-editor-dock="" aria-label={title} hidden={hidden} onKeyDown={handleKeyDown} onKeyDownCapture={onKeyDownCapture}>
      <div className="pnid-editor-dock-head">
        {onBack && (
          <button type="button" className="pnid-btn pnid-btn--sm pnid-btn--outlined" onClick={onBack}>
            ‹ Back
          </button>
        )}
        <div className="pnid-editor-dock-heading">
          <h2 className="pnid-editor-dock-title">{title}</h2>
          {subtitle && <p className="pnid-editor-dock-subtitle">{subtitle}</p>}
        </div>
        {headerActions}
        {onClose && (
          <button type="button" className="pnid-icon-btn" aria-label={`Close ${title}`} onClick={onClose}>
            <AppIcon icon={X} />
          </button>
        )}
      </div>
      <DockFooterSlotContext.Provider value={footerSlot}>
        <div className="pnid-editor-dock-body">{children}</div>
        {confirmingDiscard && (
          <div className="pnid-editor-dock-confirm" role="alertdialog" aria-label="Discard unsaved changes?">
            <span className="pnid-editor-dock-confirm-text">Discard unsaved changes?</span>
            <button type="button" className="pnid-btn pnid-btn--outlined pnid-btn--sm" autoFocus onClick={keepEditing}>
              Keep editing
            </button>
            <button type="button" className="pnid-btn pnid-btn--contained pnid-btn--sm" onClick={onBack ?? onClose}>
              Discard
            </button>
          </div>
        )}
        {footer}
      </DockFooterSlotContext.Provider>
      <div ref={setFooterSlot} />
    </aside>
  );

  if (workspaceSlot === undefined) return dock;
  return workspaceSlot ? createPortal(dock, workspaceSlot) : null;
}
